import { CommonModule } from "@angular/common";
import { Component, Input, Output, EventEmitter } from "@angular/core";
import { FormGroup, ReactiveFormsModule } from "@angular/forms";
import { CardModule } from "primeng/card";
import { CheckboxModule } from "primeng/checkbox";
import { InputTextModule } from "primeng/inputtext";
import { ButtonModule } from "primeng/button";
import { TooltipModule } from "primeng/tooltip";
import { CustomerPortalSettingsForm } from "../../../interfaces/customer-portal-settings.interface";

@Component({
  selector: "app-content-management",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CardModule,
    CheckboxModule,
    InputTextModule,
    ButtonModule,
    TooltipModule,
  ],
  template: `
    <div class="content-layout">
      <!-- Two Card Layout -->
      <div class="two-column-row">
        <!-- No Scheduled Order Settings -->
        <div class="column-item">
          <p-card>
            <ng-template pTemplate="title">
              <div class="flex items-center gap-2">
                <i class="pi pi-exclamation-triangle text-orange-600"></i>
                No Scheduled Order Settings
                <i
                  class="pi pi-info-circle text-gray-400 cursor-pointer"
                  pTooltip="Configure messages and actions when customers have no subscriptions"
                  tooltipPosition="right"
                  [style.font-size]="'0.875rem'"
                ></i>
              </div>
            </ng-template>
            <ng-template pTemplate="subtitle">
              Customize what happens when a customer does not already have a
              subscription. These settings only apply to v2 Subscriber Portal.
            </ng-template>
            <ng-template pTemplate="content">
              <div class="form-field">
                <label class="form-label" for="noOrdersBody">
                  No Scheduled Orders Body Message
                </label>
                <div class="input-group">
                  <input
                    pInputText
                    id="noOrdersBody"
                    [formControl]="formGroup.controls.noScheduleOrdersBody"
                    [maxlength]="maxLength"
                    class="form-input"
                    placeholder="Enter message for customers without subscriptions"
                  />
                  <p-button
                    icon="pi pi-refresh"
                    severity="secondary"
                    [outlined]="true"
                    size="small"
                    pTooltip="Reset to default value"
                    tooltipPosition="top"
                    (click)="loadDefault('noScheduleOrdersBody', null)"
                  />
                </div>
                <div class="field-footer">
                  <small class="character-count">
                    {{
                      formGroup.get("noScheduleOrdersBody")?.value?.length || 0
                    }}
                    / {{ maxLength }}
                  </small>
                  <small
                    *ngIf="
                      formGroup.get('noScheduleOrdersBody')?.invalid &&
                      formGroup.get('noScheduleOrdersBody')?.touched
                    "
                    class="error-message"
                  >
                    Field cannot exceed {{ maxLength }} characters
                  </small>
                </div>
              </div>

              <div class="form-field">
                <label class="form-label" for="noOrdersLink">
                  No Scheduled Orders Link
                </label>
                <div class="input-group">
                  <input
                    pInputText
                    id="noOrdersLink"
                    [formControl]="formGroup.controls.noScheduleOrdersLink"
                    [maxlength]="maxLength"
                    class="form-input"
                    placeholder="Enter link for creating new subscriptions"
                  />
                  <p-button
                    icon="pi pi-refresh"
                    severity="secondary"
                    [outlined]="true"
                    size="small"
                    pTooltip="Reset to default value"
                    tooltipPosition="top"
                    (click)="loadDefault('noScheduleOrdersLink', null)"
                  />
                </div>
                <div class="field-footer">
                  <small class="character-count">
                    {{
                      formGroup.get("noScheduleOrdersLink")?.value?.length || 0
                    }}
                    / {{ maxLength }}
                  </small>
                  <small
                    *ngIf="
                      formGroup.get('noScheduleOrdersLink')?.invalid &&
                      formGroup.get('noScheduleOrdersLink')?.touched
                    "
                    class="error-message"
                  >
                    Field cannot exceed {{ maxLength }} characters
                  </small>
                </div>
              </div>
            </ng-template>
          </p-card>
        </div>

        <!-- Products + Localization Settings Combined -->
        <div class="column-item">
          <p-card>
            <ng-template pTemplate="title">
              <div class="flex items-center gap-2">
                <i class="pi pi-box text-purple-600"></i>
                Products & Localization
                <i
                  class="pi pi-info-circle text-gray-400 cursor-pointer"
                  pTooltip="Configure product display options and customize text labels"
                  tooltipPosition="right"
                  [style.font-size]="'0.875rem'"
                ></i>
              </div>
            </ng-template>
            <ng-template pTemplate="subtitle">
              Manage product settings and customize key words for your
              templates.
            </ng-template>
            <ng-template pTemplate="content">
              <!-- Products Section -->
              <div class="section">
                <h4 class="section-title">
                  <i class="pi pi-box text-purple-600 mr-2"></i>
                  Product Settings
                </h4>
                <div class="option-item">
                  <div class="option-content">
                    <p-checkbox
                      [formControl]="
                        formGroup.controls.sortProductsAlphabetically
                      "
                      [binary]="true"
                      inputId="sortProducts"
                    />
                    <div class="option-text">
                      <label for="sortProducts" class="option-label">
                        Sort products by alphabetical order in the product drop
                        down.
                      </label>
                    </div>
                  </div>
                </div>

                <div class="option-item">
                  <div class="option-content">
                    <p-checkbox
                      [formControl]="formGroup.controls.hideOutOfStockProducts"
                      [binary]="true"
                      inputId="hideOutOfStock"
                    />
                    <div class="option-text">
                      <label for="hideOutOfStock" class="option-label">
                        Hide products that are currently out of stock from the
                        product drop down.
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Divider -->
              <div class="section-divider"></div>

              <!-- Localization Section -->
              <div class="section">
                <h4 class="section-title">
                  <i class="pi pi-globe text-green-600 mr-2"></i>
                  Localization Settings
                </h4>
                <p class="section-description">
                  Customize key words and text labels for your templates.
                </p>
                <div class="action-section">
                  <p-button
                    label="Edit Text Labels"
                    icon="pi pi-pencil"
                    severity="secondary"
                    size="small"
                    (onClick)="goToLocalization()"
                  />
                </div>
              </div>
            </ng-template>
          </p-card>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }

      .content-layout {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
        padding-bottom: 1rem;
      }

      .two-column-row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 1.5rem;
        align-items: stretch;
      }

      .column-item {
        width: 100%;
      }

      @media (max-width: 480px) {
        .content-layout {
          gap: 1rem;
        }
      }

      .section {
        margin-bottom: 1.5rem;
      }

      .section:last-child {
        margin-bottom: 0;
      }

      .section-title {
        font-size: 1rem;
        font-weight: 600;
        color: #374151;
        margin-bottom: 0.75rem;
        display: flex;
        align-items: center;
      }

      .section-description {
        color: #6b7280;
        font-size: 0.875rem;
        margin-bottom: 1rem;
        line-height: 1.4;
      }

      .section-divider {
        height: 1px;
        background: linear-gradient(
          to right,
          transparent,
          #e5e7eb 20%,
          #e5e7eb 80%,
          transparent
        );
        margin: 1.5rem 0;
        position: relative;
      }

      .section-divider::before {
        content: "";
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        width: 6px;
        height: 6px;
        background: #e5e7eb;
        border-radius: 50%;
      }

      @media (max-width: 1024px) {
        .two-column-row {
          grid-template-columns: 1fr;
          gap: 1rem;
        }
      }

      @media (max-width: 768px) {
        .content-layout {
          gap: 1rem;
        }

        .top-row {
          gap: 0.75rem;
        }
      }

      @media (max-width: 480px) {
        .two-column-row {
          gap: 0.75rem;
        }

        .form-field {
          margin-bottom: 1rem;
        }

        .form-label {
          font-size: 0.875rem;
          margin-bottom: 0.375rem;
        }

        .form-input {
          padding: 0.5rem 0.75rem;
          font-size: 0.875rem;
        }

        .input-group {
          gap: 0.375rem;
          margin-bottom: 0.375rem;
        }

        .section {
          margin-bottom: 1rem;
        }

        .section-title {
          font-size: 0.875rem;
          margin-bottom: 0.5rem;
        }

        .section-description {
          font-size: 0.8125rem;
          margin-bottom: 0.75rem;
        }

        .section-divider {
          margin: 1rem 0;
        }

        .option-item {
          padding: 0.75rem;
          margin-bottom: 0.75rem;
        }

        .option-label {
          font-size: 0.875rem;
          line-height: 1.4;
        }

        .option-content {
          gap: 0.5rem;
        }
      }

      .action-section {
        padding: 0.5rem 0;
        flex: 1;
        display: flex;
        align-items: flex-end;
      }

      .form-field {
        margin-bottom: 1.5rem;
      }

      .form-label {
        display: block;
        margin-bottom: 0.5rem;
        font-weight: 500;
        color: #374151;
      }

      .input-group {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin-bottom: 0.5rem;
      }

      .form-input {
        flex: 1;
        padding: 0.75rem;
        border: 1px solid #d1d5db;
        border-radius: 6px;
        transition: border-color 0.2s ease;
      }

      .form-input:focus {
        border-color: #3b82f6;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
      }

      .field-footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
        flex-wrap: wrap;
        gap: 0.5rem;
      }

      .character-count {
        color: #6b7280;
        font-size: 0.875rem;
      }

      .error-message {
        color: #ef4444;
        font-size: 0.875rem;
      }

      .option-item {
        padding: 1rem;
        border: 1px solid #e9ecef;
        border-radius: 8px;
        margin-bottom: 1rem;
        background-color: #f8f9fa;
        transition: all 0.2s ease;
      }

      .option-item:hover {
        background-color: #e9ecef;
        border-color: #dee2e6;
      }

      .option-item:last-child {
        margin-bottom: 0;
      }

      .option-content {
        display: flex;
        align-items: flex-start;
        gap: 0.75rem;
      }

      .option-text {
        flex: 1;
      }

      .option-label {
        display: block;
        cursor: pointer;
        font-weight: 500;
        color: #374151;
        line-height: 1.5;
        margin-bottom: 0;
      }

      :host ::ng-deep .p-card {
        height: 100%;
        box-shadow:
          0 1px 3px 0 rgb(0 0 0 / 0.1),
          0 1px 2px -1px rgb(0 0 0 / 0.1);
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        display: flex;
        flex-direction: column;
      }

      :host ::ng-deep .compact-card .p-card-subtitle {
        font-size: 0.8rem;
        line-height: 1.4;
      }

      :host ::ng-deep .p-card .p-card-header {
        border-bottom: 1px solid #e5e7eb;
        padding: 1.25rem;
      }

      :host ::ng-deep .p-card .p-card-content {
        padding: 1.25rem;
        flex: 1;
        display: flex;
        flex-direction: column;
      }

      :host ::ng-deep .p-card .p-card-title {
        font-size: 1.125rem;
        font-weight: 600;
        color: #111827;
      }

      :host ::ng-deep .p-card .p-card-subtitle {
        color: #6b7280;
        font-size: 0.875rem;
        margin-top: 0.25rem;
      }

      :host ::ng-deep .p-checkbox .p-checkbox-box {
        border-radius: 4px;
      }

      :host ::ng-deep .p-inputtext {
        border-radius: 6px;
        border: 1px solid #d1d5db;
        transition: border-color 0.2s ease;
      }

      :host ::ng-deep .p-inputtext:enabled:focus {
        border-color: #3b82f6;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
      }

      @media (max-width: 480px) {
        :host ::ng-deep .p-card .p-card-content {
          padding: 0;
        }

        :host ::ng-deep .p-card .p-card-title {
          font-size: 1rem;
        }

        :host ::ng-deep .p-card .p-card-subtitle {
          font-size: 0.8125rem;
          line-height: 1.3;
        }

        :host ::ng-deep .p-card .p-card-header {
          padding: 0.875rem;
        }
      }
    `,
  ],
})
export class ContentManagementComponent {
  @Input({ required: true }) formGroup!: FormGroup<CustomerPortalSettingsForm>;
  @Input({ required: true }) isLoading!: boolean;
  @Input({ required: true }) maxLength!: number;

  @Output() goToLocalizationOutput = new EventEmitter<void>();
  @Output() loadDefaultOutput = new EventEmitter<{
    field: string;
    value: string | null;
  }>();

  goToLocalization(): void {
    this.goToLocalizationOutput.emit();
  }

  loadDefault(field: string, value: string | null): void {
    this.loadDefaultOutput.emit({ field, value });
  }
}
